/* ============================================================
   CHRONCES — Comportements du header
   Web Components vanilla (convention Horizon), aucune dépendance.
   Maquette : design-source/2026-07-31/Navigation.dc.html
   ============================================================ */

/* ------------------------------------------------------------
   <cr-header> — nav desktop (dropdowns) + drawer mobile
   ------------------------------------------------------------ */
class CrHeader extends HTMLElement {
  connectedCallback() {
    this.navItems = Array.from(this.querySelectorAll('[data-cr-nav-item]'));
    this.navItems.forEach((item) => this.bindNavItem(item));

    // Fermetures globales
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocumentClick);

    this.bindMobile();
    this.bindSticky();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocumentClick);
    this.stickyObserver?.disconnect();
    this.unlockScroll();
  }

  /* ---------- En-tête collant ----------
     Le collage lui-même est en CSS (voir cr-header.css) ; ici on ne fait que
     mesurer la barre d'annonce, pour que le groupe remonte exactement de sa
     hauteur et laisse l'en-tête seul épinglé. Sa hauteur n'est pas figée : elle
     dépend du message, qui peut passer sur deux lignes en mobile. */
  bindSticky() {
    if (this.dataset.sticky !== 'true') return;

    const group = this.closest('#header-group');
    const bar = group?.querySelector('.cr-ann');
    if (!group || !bar) return;

    // La hauteur de l'en-tête est publiée sur <html> : les barres collantes
    // des pages (constructeur de packs…) s'en servent pour se poser juste
    // dessous. Elle est mesurée, jamais figée — le logo mobile et le desktop
    // n'ont pas la même hauteur.
    const header = this.querySelector('.cr-header') || this;

    const apply = () => {
      group.style.setProperty('--cr-header-offset', `${Math.round(bar.offsetHeight)}px`);
      document.documentElement.style.setProperty('--cr-header-height', `${Math.round(header.offsetHeight)}px`);
    };
    apply();

    if ('ResizeObserver' in window) {
      this.stickyObserver = new ResizeObserver(apply);
      this.stickyObserver.observe(bar);
      this.stickyObserver.observe(header);
    }
  }

  /* ---------- Nav desktop ---------- */
  bindNavItem(item) {
    const trigger = item.querySelector('[data-cr-nav-trigger]');
    if (!trigger) return;

    // Survol : ouverture immédiate, fermeture légèrement différée pour
    // laisser le temps d'atteindre le panneau à la souris.
    item.addEventListener('mouseenter', () => {
      clearTimeout(item._closeTimer);
      this.openNav(item);
    });
    item.addEventListener('mouseleave', () => {
      item._closeTimer = setTimeout(() => this.closeNav(item), 120);
    });

    // Clic : indispensable pour le tactile et le clavier
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      item.classList.contains('is-open') ? this.closeNav(item) : this.openNav(item);
    });

    // Le focus clavier sortant du groupe referme
    item.addEventListener('focusout', (event) => {
      if (!item.contains(event.relatedTarget)) this.closeNav(item);
    });
  }

  openNav(item) {
    this.navItems.forEach((other) => {
      if (other !== item) this.closeNav(other);
    });
    item.classList.add('is-open');
    item.querySelector('[data-cr-nav-trigger]')?.setAttribute('aria-expanded', 'true');
  }

  closeNav(item) {
    clearTimeout(item._closeTimer);
    item.classList.remove('is-open');
    item.querySelector('[data-cr-nav-trigger]')?.setAttribute('aria-expanded', 'false');
  }

  closeAllNav() {
    this.navItems.forEach((item) => this.closeNav(item));
  }

  onDocumentClick = (event) => {
    if (!this.contains(event.target)) this.closeAllNav();
  };

  onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (this.mobileOpen) {
      this.closeMobile();
      this.openButton?.focus();
    } else {
      this.closeAllNav();
    }
  };

  /* ---------- Drawer mobile ---------- */
  bindMobile() {
    this.drawer = this.querySelector('[data-cr-mobile]');
    this.overlay = this.querySelector('[data-cr-mobile-overlay]');
    this.openButton = this.querySelector('[data-cr-mobile-open]');
    if (!this.drawer || !this.openButton) return;

    this.openButton.addEventListener('click', () => this.openMobile());
    this.overlay?.addEventListener('click', () => this.closeMobile());
    this.querySelector('[data-cr-mobile-close]')?.addEventListener('click', () => {
      this.closeMobile();
      this.openButton.focus();
    });

    // Accordéons
    this.drawer.querySelectorAll('[data-cr-acc-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const panel = trigger.nextElementSibling;
        if (!panel) return;
        const open = trigger.getAttribute('aria-expanded') === 'true';
        trigger.setAttribute('aria-expanded', String(!open));
        panel.hidden = open;
        const sign = trigger.querySelector('[data-cr-acc-sign]');
        if (sign) sign.textContent = open ? '+' : '–';
      });
    });

    // Le drawer mobile n'a pas lieu d'être ouvert en desktop
    this.desktop = window.matchMedia('(min-width: 990px)');
    this.desktop.addEventListener('change', (event) => {
      if (event.matches && this.mobileOpen) this.closeMobile();
    });
  }

  openMobile() {
    this.mobileOpen = true;
    this.drawer.hidden = false;
    if (this.overlay) this.overlay.hidden = false;
    this.openButton.setAttribute('aria-expanded', 'true');
    this.lockScroll();
    this.drawer.querySelector('[data-cr-mobile-close]')?.focus();
    document.addEventListener('keydown', this.trapFocus);
  }

  closeMobile() {
    this.mobileOpen = false;
    this.drawer.hidden = true;
    if (this.overlay) this.overlay.hidden = true;
    this.openButton.setAttribute('aria-expanded', 'false');
    this.unlockScroll();
    document.removeEventListener('keydown', this.trapFocus);
  }

  // Piège le focus dans le drawer tant qu'il est ouvert
  trapFocus = (event) => {
    if (event.key !== 'Tab' || !this.mobileOpen) return;
    const focusables = this.drawer.querySelectorAll(
      'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  lockScroll() {
    document.body.style.overflow = 'hidden';
  }

  unlockScroll() {
    document.body.style.overflow = '';
  }
}

if (!customElements.get('cr-header')) {
  customElements.define('cr-header', CrHeader);
}
